// Real CLEMENT COURSE I product imagery — served via backend proxy
// (same origin as the React app so ad-blockers / DNS filters that
// might block customer-assets.emergentagent.com can't break it).

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const asset = (key) => `${API}/asset/${key}`;

// Club Edition premium production spec sheet (shared base for Azure & Chameleon)
function CLUB_PREMIUM_SPECS(reference) {
  return {
    reference,
    case: "Grade 5 titanium or 904L stainless steel · 41.0 mm Ø · 47.0 mm lug-to-lug · 11.8–12.4 mm thick · softened shield-octagon with polished chamfers",
    crown: "Solid screw-down dimpled golf-ball crown · 6.5 mm diameter · 3.4–4.0 mm projection",
    bezel: "Ceramic / 18K gold / hardened steel option · plays-like-yardage 335° scale · 40.5–41.0 mm OD · 3.2–3.8 mm visible width",
    crystal: "Sapphire with internal anti-reflective coating",
    dial: "30.8–31.5 mm · applied CLEMENT branding & hour markers · sunken subdials · textured base (contour-map / guilloché / lacquer / scenic)",
    caseback: "Sapphire exhibition · engraved CLEMENT · COURSE I · model · serial · edition number · AUTOMATIC · SAPPHIRE CRYSTAL · 100M",
    movement: "Swiss Automatic Top Grade · Sellita SW300-1 or SW200-1 (COSC-grade target) · 28,800 vph · ~38–60 hour reserve · custom CLEMENT rotor",
    complications: "Plays-like yardage bezel · Beaufort wind reference · pusher-operated stroke counter with sequential 1 → 10 register · slope shot-adjustment (UP/DOWN ±25/±50)",
    hands: "Club-shaped hour & minute · tee-shaped seconds with red tip",
    markers: "Applied indices with premium finish",
    bracelet: "Integrated H-link bracelet · 24 mm at case tapering to 18 mm at clasp · screw links · quick-release",
    waterResistance: "100 m",
    production: "Limited edition · individually numbered · scratch-resistant finishes · Swiss Made target (supplier-confirmed)",
  };
}

// Launch Edition spec — 1,500 pieces · 18K precious metals
function LAUNCH_SPECS({ code, dial, metal, caseDesc }) {
  return {
    reference: `${code} · serials ${code}-0001 to ${code}-1500`,
    case: `${caseDesc} · 41.0 mm Ø · 47.0 mm lug-to-lug · 11.8–12.4 mm thick · softened shield-octagon · polished chamfers`,
    crown: "Solid screw-down dimpled golf-ball crown · 6.5 mm diameter · 3.4–4.0 mm projection",
    bezel: "Plays-like-yardage 335° scale · 40.5–41.0 mm OD · 3.2–3.8 mm visible width · machined to match case finish",
    crystal: "Sapphire with internal anti-reflective coating",
    dial: `${dial} · 30.8–31.5 mm · applied CLEMENT branding · sunken subdials · CLEMENT · COURSE I · LAUNCH EDITION`,
    caseback: `Sapphire exhibition · engraved CLEMENT · COURSE I · ${code} · No. 001 → 1500 · AUTOMATIC · ${metal.toUpperCase()} · SAPPHIRE CRYSTAL · 100M`,
    movement: "Swiss Automatic Top Grade · Sellita SW300-1 or SW200-1 (COSC-grade option) · 28,800 vph · ~38–56 hour reserve · custom CLEMENT rotor",
    complications: "Plays-like yardage bezel · Beaufort wind reference · pusher-operated stroke counter (1 → 10) · slope shot-adjustment (UP/DOWN ±25/±50)",
    hands: "Club-shaped hour & minute · tee-shaped seconds with red tip",
    markers: "Applied indices · precious-metal finish to match case",
    bracelet: `Integrated H-link bracelet in ${metal} · 24 mm at case tapering to 18 mm at clasp · screw links · quick-release`,
    waterResistance: "100 m production target",
    production: `1,500 pieces · individually numbered ${code}-0001 → ${code}-1500 · Swiss Made target (supplier-confirmed)`,
  };
}

// Men's Scenic Dial spec — limited capsule, MS series
function SCENIC_DIAL_SPECS({ code, name, scene, dialColor }) {
  return {
    reference: `${code} · ${name} · limited scenic capsule`,
    case: "904L stainless steel or Grade 5 titanium · 39–41 mm Ø · 47 mm lug-to-lug · 12.5–13.5 mm thick · softened shield-octagon",
    crown: "Solid screw-down dimpled golf-ball crown · 6.5 mm diameter · 3.4–4.0 mm projection",
    bezel: "Engraved plays-like-yardage scale · Beaufort wind reference · brushed/polished alternation · 40.5–41.0 mm OD",
    crystal: "Sapphire with internal anti-reflective coating",
    dial: `Hand-painted miniature — ${scene} · ${dialColor} · CLEMENT · COURSE I · SCENIC DIAL · SWISS MADE signature · 30.8–31.5 mm`,
    caseback: `Sapphire exhibition · engraved CLEMENT · COURSE I · ${code} · individual serial · AUTOMATIC · SAPPHIRE CRYSTAL · 100M`,
    movement: "Swiss automatic premium option · Sellita SW200-1 or SW300-1 · 28,800 vph · ~38–56 hour reserve",
    complications: "Plays-like yardage bezel · Beaufort wind reference · stroke counter (1 → 10) · slope shot-adjustment (UP/DOWN ±25/±50)",
    hands: "Club-shaped hour & minute · tee-shaped seconds with luminous tip",
    markers: "Applied indices · readable over scenic artwork without clutter",
    bracelet: "Integrated H-link bracelet · 24 mm at case tapering to 18 mm at clasp · brushed/polished · screw links",
    waterResistance: "100 m production target",
    production: "Limited scenic capsule · quantity TBD · Swiss Made target",
  };
}

// Women's Scenic Petite spec — 28 / 30 / 32 mm platform
// Sourced verbatim from 07-womens-scenic-slim-spec.md
// Family priorities: wrist elegance → thinness → jewelry-bracelet feel → dial beauty
function PETITE_SPECS({ code, name, scene, handColor, bracelet }) {
  return {
    reference: `${code} · ${name} · CLEMENT COURSE I Scenic Petite family`,
    case: "316L stainless steel · polished / brushed mix · optional rose-gold or white-gold tone PVD · softer case edges · slimmer shield-octagon bezel · 28 / 30 / 32 mm Ø · 34–38 mm lug-to-lug · 8.5–9.8 mm thick · 32 mm is the recommended first prototype size",
    crown: "Scaled-down dimpled golf-ball crown · 3.8–4.8 mm diameter (size-dependent) · lower-profile crown guards",
    bezel: "Slim shield-octagon · jewelry-house facet work · soft brushed surfaces · feminine proportion",
    crystal: "Sapphire with single-sided anti-reflective coating",
    dial: `Printed ${scene} · gloss or satin protective lacquer · applied markers (budget permitting) · layout: 12 o'clock CLEMENT branding, 3 o'clock stroke counter, 6 o'clock scenic artwork focus, 9 o'clock wind reference · no slope arc`,
    caseback: `Solid closed caseback · engraved CLEMENT · COURSE I · ${code} · individual serial · SCENIC PETITE`,
    movement: "Recommended: small quartz or meca-quartz for ultra-thin first prototype (Ronda 715 / Soprod M100 / ETA F06) · upgrade path to small Swiss automatic if supplier can keep thickness under control",
    complications: "Timekeeping · stroke counter (1 → 10 register) · wind reference register · NO slope · NO inclinometer arc · no heavy tool-watch density",
    hands: `Club-shaped hour & minute · tee-shaped seconds with matching accent tip · ${handColor}`,
    markers: "Applied baton or jewel-set indices · lacquered to match dial scene",
    bracelet: `${bracelet} · slim jewelry-bracelet proportions · articulated polished links · soft oval / rice-grain / refined rectangular links · ${"width 14–17 mm at case tapering to 10–14 mm at clasp (size-dependent)"} · optional hidden butterfly clasp · polished white-gold tone, rose-gold tone or two-tone finish`,
    waterResistance: "30–50 m prototype · 50–100 m production target",
    production: "Limited Scenic Petite capsule · individually numbered · jewelry-watch construction · 32 mm Statement Petite first sample · 28 mm jewelry / 30 mm classic follow-up · Swiss Made target",
  };
}

export const WATCH_IMAGES = {
  rosewoodCharcoal: {
    slug: "rosewood-charcoal",
    name: "Rosewood Charcoal",
    ref: "CRS-I · RG",
    model: "CC1-RG-002",
    metal: "18K Rose Gold",
    notes: "Rose gold case · charcoal dial · solid rose gold bracelet",
    front: asset("rosewood-charcoal"),
    back: asset("rosewood-charcoal-back"),
    specs: LAUNCH_SPECS({
      code: "CC1-RG",
      dial: "Charcoal dial with rose-gold warmth",
      metal: "18K rose gold",
      caseDesc: "18K rose gold (production target) · plated visual sample option",
    }),
  },
  champagneGreen: {
    slug: "champagne-green",
    name: "Champagne Green",
    ref: "CRS-I · WG",
    model: "CC1-WG-004",
    metal: "18K White Gold",
    notes: "White gold case · champagne-green guilloché dial · matching bracelet",
    front: asset("champagne-green"),
    back: asset("champagne-green-back"),
    specs: LAUNCH_SPECS({
      code: "CC1-WG",
      dial: "Champagne dial with green accents · guilloché texture",
      metal: "18K white gold",
      caseDesc: "18K white gold (production target) · plated visual sample option",
    }),
  },
  goldenFairway: {
    slug: "golden-fairway",
    name: "Golden Fairway",
    ref: "CRS-I · YG",
    model: "CC1-YG-001",
    metal: "18K Yellow Gold",
    notes: "Yellow gold case · champagne dial · yellow gold bracelet",
    front: asset("golden-fairway"),
    back: asset("golden-fairway-back"),
    specs: LAUNCH_SPECS({
      code: "CC1-YG",
      dial: "Emerald green premium dial",
      metal: "18K yellow gold",
      caseDesc: "18K yellow gold (production target) · PVD visual sample option",
    }),
  },
  midnightLinks: {
    slug: "midnight-links",
    name: "Midnight Links",
    ref: "CRS-I · GC",
    model: "CC1-GC-003",
    metal: "18K Gold + Ceramic",
    notes: "Gold + ceramic case · deep green dial · two-tone bracelet",
    front: asset("midnight-links"),
    back: asset("midnight-links-back"),
    specs: LAUNCH_SPECS({
      code: "CC1-GC",
      dial: "Black / dark private-club dial · deep green accents",
      metal: "18K gold + black ceramic",
      caseDesc: "18K gold and black ceramic (production direction)",
    }),
  },
};

export const LAUNCH_EDITIONS = [
  WATCH_IMAGES.rosewoodCharcoal,
  WATCH_IMAGES.champagneGreen,
  WATCH_IMAGES.goldenFairway,
  WATCH_IMAGES.midnightLinks,
];

// Editorial / lifestyle photography supplied by CLEMENT
export const SCENES = {
  landingHero: asset("landing-hero"),
  terrace: asset("scene-terrace"),
  scorecard: asset("scene-scorecard"),
  petiteMagnolia: asset("scene-petite-magnolia"),
  displayCase: asset("scene-display-case"),
  petiteWrist: asset("scene-petite-wrist"),
};

// Scenic Editions — hand-finished landscape dials
export const SCENIC_EDITIONS = [
  {
    slug: "heritage-fairway",
    name: "Heritage Fairway",
    ref: "CRS-I · MS1",
    line: "Scenic Dial",
    metal: "Stainless Steel · 41 mm",
    notes:
      "Painted parkland fairway and emerald green dial · classic links composition · signature compass and golf-ball crown",
    front: asset("scenic-heritage-fairway"),
    specs: SCENIC_DIAL_SPECS({
      code: "CC1-MS1",
      name: "Heritage Fairway",
      scene: "championship fairway, green and bunkers in classic links composition",
      dialColor: "emerald green base with parkland greens and earth tones",
    }),
  },
  {
    slug: "ocean-cliffs",
    name: "Ocean Cliffs",
    ref: "CRS-I · MS2",
    line: "Scenic Dial",
    metal: "Stainless Steel · 41 mm",
    notes:
      "Headland green over the Pacific · blue lacquer sky, mineral cliffs · for the dramatic coastal course",
    front: asset("scenic-ocean-cliffs"),
    specs: SCENIC_DIAL_SPECS({
      code: "CC1-MS2",
      name: "Ocean Cliffs",
      scene: "headland golf hole over the Pacific · mineral cliffs and surf",
      dialColor: "blue lacquer sky with mineral grey-green cliff face",
    }),
  },
  {
    slug: "turquoise-coast",
    name: "Turquoise Coast",
    ref: "CRS-I · MS · Turquoise",
    line: "Scenic Dial",
    metal: "Stainless Steel · 41 mm",
    notes:
      "Tropical headland in the Caribbean · turquoise lume and bezel inlay · for the resort-course collector",
    front: asset("scenic-turquoise-coast"),
    specs: SCENIC_DIAL_SPECS({
      code: "CC1-MS · Turquoise",
      name: "Turquoise Coast",
      scene: "tropical Caribbean headland · resort fairway and crystalline water",
      dialColor: "turquoise lacquer sea · sand & emerald scenic accents",
    }),
  },
  {
    slug: "desert-bloom",
    name: "Desert Bloom",
    ref: "CRS-I · WS5",
    line: "Scenic Petite",
    metal: "Stainless Steel · 32 mm",
    notes:
      "Sonoran sunset landscape · violet, teal and gold hands · cactus and wildflowers framing a hidden green",
    front: asset("scenic-petite-desert-bloom"),
    specs: PETITE_SPECS({
      code: "CC1-WS5",
      name: "Desert Bloom",
      scene: "Sonoran sunset · cactus and wildflowers framing a hidden green",
      handColor: "purple and turquoise lacquer hands",
      bracelet: "Polished two-tone or rose-gold tone jewelry bracelet · taper 16 → 12 mm · concealed butterfly clasp",
    }),
  },
  {
    slug: "pearl-clubhouse",
    name: "Pearl Clubhouse",
    ref: "CRS-I · WS6",
    line: "Scenic Petite",
    metal: "Stainless Steel · 32 mm",
    notes:
      "Mother-of-pearl sky over the clubhouse · soft pink markers · the most jewelry-watch of the line",
    front: asset("scenic-petite-pearl-clubhouse"),
    specs: PETITE_SPECS({
      code: "CC1-WS6",
      name: "Pearl Clubhouse",
      scene: "elegant clubhouse and manicured green under a mother-of-pearl sky",
      handColor: "pearl-white, soft-pink and turquoise lacquer hands",
      bracelet: "Pearl / cabochon-style jewelry bracelet direction · taper 16 → 12 mm · concealed butterfly clasp",
    }),
  },
  {
    slug: "garden-green",
    name: "Garden Green",
    ref: "CRS-I · WS4",
    line: "Scenic Petite",
    metal: "Stainless Steel · 32 mm",
    notes:
      "Rose garden landscape with a gazebo on the green · rose-gold accents and markers · putter and iron shaped hands",
    front: asset("scenic-petite-garden-green"),
    specs: PETITE_SPECS({
      code: "CC1-WS4",
      name: "Garden Green",
      scene: "floral rose-garden course scene with a gazebo on the green",
      handColor: "pink and rose-gold lacquer hands",
      bracelet: "Polished rose-gold or two-tone jewelry bracelet · taper 16 → 12 mm · concealed butterfly clasp",
    }),
  },
  {
    slug: "magnolia-pearl",
    name: "Magnolia Pearl",
    ref: "CRS-I · WS7 · Pearl",
    line: "Scenic Petite",
    metal: "Two-Tone · Pearl Bracelet · 32 mm",
    notes:
      "Oval mother-of-pearl case · cultured pearl & rose-quartz bracelet · the boudoir of the Scenic Petite line",
    front: asset("scenic-petite-magnolia-pearl"),
    specs: PETITE_SPECS({
      code: "CC1-WS7 · Pearl",
      name: "Magnolia Pearl",
      scene: "magnolia blossoms over a classic clubhouse green · mother-of-pearl sky",
      handColor: "pearl-white and soft-pink lacquer hands",
      bracelet: "Cultured pearl & rose-quartz jewelry bracelet · taper 14 → 11 mm · concealed butterfly clasp",
    }),
  },
  {
    slug: "orchid-fairway",
    name: "Orchid Fairway",
    ref: "CRS-I · MS · Orchid",
    line: "Scenic Dial",
    metal: "316L Stainless Steel · 41 mm",
    notes:
      "Hand-painted resort fairway at dusk · violet orchids in bloom · amethyst baguette markers · turquoise seconds and lume",
    front: asset("scenic-orchid-fairway"),
    specs: {
      reference: "CC1-BD-P001 · Orchid Fairway",
      case: "316L stainless steel · 41 mm Ø · 47 mm lug-to-lug · 12.5–13.5 mm thick · approx. 145 g full bracelet",
      dial: "Hand-painted miniature — resort fairway, lagoon, clubhouse at dusk with violet orchids · printed CLEMENT · COURSE I · SCENIC DIAL signature",
      bezel: "Fixed plays-like-yardage scale (0 to ±330) · Beaufort wind reference (Bft 0–6) · brushed steel with violet lume inlays",
      crystal: "Sapphire, double-sided anti-reflective (prototype option: mineral)",
      caseback: "Solid display caseback · engraved compass rose · individual CLEMENT serial",
      movement: "Sellita SW200-1 automatic (Seiko/TMI NH35 alternate) · 38-hour reserve · 28,800 vph",
      complications: "Compass at 12 · stroke counter at 3 · slope shot-adjustment ±25/+50 at 6 · Beaufort wind reference at 9",
      hands: "Putter hour · iron minute · turquoise central seconds · luminous violet on hours/minutes",
      markers: "Applied amethyst baguette indices with violet lume",
      bracelet: "Integrated 24 mm shield-link bracelet, taper to 18 mm at clasp · brushed/polished alternation · butterfly clasp",
      waterResistance: "100 m production target · 50 m prototype",
      production: "Limited run · Swiss Made target",
    },
  },
  {
    slug: "magnolia-green",
    name: "Magnolia Green",
    ref: "CRS-I · WS7 · Magnolia",
    line: "Scenic Petite",
    metal: "Stainless Steel + Mother-of-Pearl · 32 mm",
    notes:
      "White magnolias over a classic American clubhouse · pink lacquer accents · mother-of-pearl bracelet inserts · jewelry-watch proportions",
    front: asset("scenic-petite-magnolia-green"),
    specs: {
      reference: "CC1-WS7 · Magnolia Green",
      case: "316L stainless steel (optional rose-gold or white-gold PVD) · 32 mm Ø (also 28 / 30 mm) · 34–38 mm lug-to-lug · 8.5–9.8 mm thick · approx. 78 g",
      dial: "Hand-painted parkland green and clubhouse with white magnolias in full bloom · CLEMENT · COURSE I · SCENIC DIAL · SWISS MADE",
      bezel: "Slim shield-octagon · plays-like-yardage (0 to ±330) · Beaufort wind (Bft 0–6) · soft brushed with rose-pink lume rims",
      crystal: "Sapphire, single-sided anti-reflective",
      caseback: "Closed solid caseback · engraved magnolia & compass rose · individual serial",
      movement: "Meca-quartz or ultra-thin Swiss automatic (Ronda 715 / Soprod M100) — selected for slimness",
      complications: "Timekeeping · stroke counter at 3 (sequential 1 → 10 register) · slope shot-adjustment at 6 · Beaufort wind reference at 9",
      hands: "Putter hour · driver minute · slim baton seconds · pink lacquer with mother-of-pearl tips",
      markers: "Applied baton indices · lacquered rose-pink with mother-of-pearl inlays",
      bracelet: "Jewelry-link bracelet with mother-of-pearl center inserts · taper 18 → 14 mm · concealed butterfly clasp",
      waterResistance: "50–100 m production target",
      production: "Limited run · Swiss Made target",
    },
  },
  {
    slug: "alpine-hour",
    name: "Alpine Hour",
    ref: "CRS-I · MS · Alpine",
    line: "Scenic Dial",
    metal: "Stainless Steel · 41 mm",
    notes:
      "Hand-painted alpine fairway and snow-capped peaks · cool steel-blue lacquer sky · silver lume markers for high-altitude rounds",
    front: asset("scenic-alpine-hour"),
    specs: SCENIC_DIAL_SPECS({
      code: "CC1-MS3",
      name: "Alpine Hour",
      scene: "mountain course at golden hour · snow-capped peaks behind an alpine fairway",
      dialColor: "cool steel-blue lacquer sky · pine-green and slate-grey scenic tones",
    }),
  },
  {
    slug: "rose-dawn",
    name: "Rose Dawn",
    ref: "CRS-I · WS1",
    line: "Scenic Petite",
    metal: "Stainless Steel · 32 mm",
    notes:
      "Sunrise-pink lacquer sky over the first tee · rose-gold accents · soft mother-of-pearl highlights · the earliest round of the morning",
    front: asset("scenic-petite-rose-dawn"),
    specs: PETITE_SPECS({
      code: "CC1-WS1",
      name: "Rose Dawn",
      scene: "rose sunrise fairway · the earliest round of the morning",
      handColor: "pink club hands",
      bracelet: "Polished white-gold tone, rose-gold tone or steel jewelry bracelet · taper 16 → 12 mm · concealed butterfly clasp",
    }),
  },
  {
    slug: "lavender-links",
    name: "Lavender Links",
    ref: "CRS-I · WS2",
    line: "Scenic Petite",
    metal: "Stainless Steel · 32 mm",
    notes:
      "Coastal lavender fields above a links fairway · pale-violet lacquer dial · silver-lume baton markers · a Provençal afternoon on the wrist",
    front: asset("scenic-petite-lavender-links"),
    specs: PETITE_SPECS({
      code: "CC1-WS2",
      name: "Lavender Links",
      scene: "lavender sunset golf course · Provençal fields above a links fairway",
      handColor: "purple club hands",
      bracelet: "Polished steel or white-gold tone jewelry bracelet · taper 16 → 12 mm · concealed butterfly clasp",
    }),
  },
  {
    slug: "jewel-bracelet",
    name: "Jewel Bracelet",
    ref: "CRS-I · SP · Jewel",
    line: "Scenic Petite",
    metal: "Two-Tone · Gemstone Bracelet · 30 mm",
    notes:
      "Jewelry-grade Petite with hand-set gemstone bracelet inserts · polished case · scenic petite dial · the dress-watch of the COURSE I women's line",
    front: asset("scenic-petite-jewel-bracelet"),
    specs: PETITE_SPECS({
      code: "CC1-SP",
      name: "Jewel Bracelet",
      scene: "scenic petite hero dial · pastel sky and manicured green",
      handColor: "pearl, blush and turquoise lacquer hands",
      bracelet: "Jewelry bracelet with hand-set pearl / blush / turquoise gemstone accent inserts · taper 16 → 12 mm · concealed butterfly clasp · 32 mm first sample · 28 / 30 mm follow-up",
    }),
  },
  {
    slug: "slim-concept",
    name: "Petite Slim",
    ref: "CRS-I · SP · Slim",
    line: "Scenic Petite",
    metal: "Stainless Steel · 30 mm · Slim Profile",
    notes:
      "Ultra-slim profile Scenic Petite concept · refined case proportions · meca-quartz movement for jewelry-watch thinness · everyday wear",
    front: asset("scenic-petite-slim-concept"),
    specs: PETITE_SPECS({
      code: "CC1-SP-SLIM",
      name: "Scenic Petite Slim",
      scene: "slim-format scenic petite dial · refined and minimal painted course",
      handColor: "slim lacquer hands matched to dial palette",
      bracelet: "Slimmer jewelry bracelet · refined slim links · taper 15 → 11 mm · concealed butterfly clasp · 28 / 30 / 32 mm",
    }),
  },
];

// Scenic Petite — 28 / 30 / 32 mm size suite showcase
export const SCENIC_PETITE_SIZE_SUITE = {
  src: asset("scenic-petite-size-suite"),
  title: "The Petite Suite",
  caption: "28 · 30 · 32 mm — rose gold case · pearl bracelet",
};

// Lifestyle / editorial campaign — "The CLEMENT Set"
export const LIFESTYLE = {
  terraceChampagne: {
    slug: "terrace-champagne",
    src: asset("lifestyle-terrace-champagne"),
    title: "On the terrace, after 18",
    caption: "Golden hour at the clubhouse",
  },
  ladiesCart: {
    slug: "ladies-cart",
    src: asset("lifestyle-ladies-cart"),
    title: "Tee-off at sunrise",
    caption: "No rush, no phones, 18 holes ahead",
  },
  firstTee: {
    slug: "first-tee",
    src: asset("lifestyle-first-tee"),
    title: "Hole one, Midnight Links",
    caption: "Bag on the cart, caddie ready, watch wound",
  },
  coupleSunset: {
    slug: "couple-sunset",
    src: asset("lifestyle-couple-sunset"),
    title: "Walking back together",
    caption: "Eighteen finished, dinner ahead",
  },
  cartRider: {
    slug: "cart-rider",
    src: asset("lifestyle-cart-rider"),
    title: "Between holes",
    caption: "The quiet stretch you actually look forward to",
  },
};

export const LIFESTYLE_LIST = Object.values(LIFESTYLE);

// Scenic Petite — lifestyle / wrist editorial
export const PETITE_LIFESTYLE = [
  {
    slug: "fairway-cuff",
    src: asset("petite-life-fairway-cuff"),
    title: "Fairway, cream cuff",
    caption: "Magnolia Pearl on the wrist · clubhouse at sunset",
  },
  {
    slug: "scorecard-terrace",
    src: asset("petite-life-scorecard-terrace"),
    title: "Marking the card",
    caption: "Magnolia Pearl · roses · 36 on the front nine",
  },
  {
    slug: "member-putter",
    src: asset("petite-life-member-putter"),
    title: "Putter in hand",
    caption: "Two-tone Petite · last green of the morning",
  },
];

export const CLUB_EDITIONS = [
  {
    slug: "azure",
    name: "Azure",
    ref: "CRS-I · BD",
    model: "CC1-BD",
    metal: "Stainless Steel",
    pieces: "1 of 100",
    notes:
      "Stainless steel case · azure-blue guilloché dial · sculpted hands referencing a putter and an iron",
    front: asset("azure"),
    specs: CLUB_PREMIUM_SPECS("CC1-BD · Azure"),
  },
  {
    slug: "chameleon",
    name: "Chameleon",
    ref: "CRS-I · CD",
    model: "CC1-CD",
    metal: "Stainless Steel",
    pieces: "1 of 100",
    notes:
      "Stainless steel case · iridescent multi-spectrum dial · contour topographic engraving",
    front: asset("chameleon"),
    specs: CLUB_PREMIUM_SPECS("CC1-CD · Chameleon"),
  },
  {
    slug: "gilded",
    name: "Gilded",
    ref: "CRS-I · GD",
    model: "CC1-GD",
    metal: "18K Gold + Stainless Steel",
    pieces: "1 of 100",
    notes:
      "Two-tone gilded case · warm champagne guilloché dial · gold-finished bezel and crown · the dress-edition of the Club line",
    front: asset("club-gilded"),
    specs: CLUB_PREMIUM_SPECS("CC1-GD · Gilded"),
  },
];

// Premium production schematic references (Club Edition standard)
export const CLUB_SCHEMATICS = {
  side: asset("club-schematic-side"),
  exploded: asset("club-schematic-exploded"),
};

export const DESIGN_ARCHIVE = [
  {
    slug: "blue-bezel",
    name: "Blue Bezel Study",
    ref: "CC1-BB",
    notes: "Sapphire-blue bezel · Plays-Like-Yardage scale prototype",
    front: asset("archive-blue-bezel"),
  },
  {
    slug: "color-shift",
    name: "Color-Shift Bezel Study",
    ref: "CC1-CS",
    notes: "Anodised spectrum bezel · titanium case prototype",
    front: asset("archive-color-shift"),
  },
  {
    slug: "gold-bezel",
    name: "Gold Bezel Exclusive",
    ref: "CC1-GB",
    notes: "Yellow gold bezel · black PVD case · gold golf-ball crowns",
    front: asset("archive-gold-bezel"),
  },
];
